import { Component, inject, input, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { KENDO_BUTTON, KENDO_DROPDOWNBUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { KENDO_GRID } from '@progress/kendo-angular-grid';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { AbstractGridComponent } from '@unity/abstract/abstract-grid.component';
import { TransitionRequestDto, TransitionsClient, UserRoleMiniDto, UserRolesClient, UserRoleUpdateDto } from '@unity/clients';

import { KENDO_STACKLAYOUT } from '@progress/kendo-angular-layout';
import { AuthorizeDirective } from '@unity/directives/authorize.directive';
import { TypedDirective } from '@unity/directives/typed.directive';
import { GridSharedComponent } from '@unity/shared/composed/grid-shared/grid-shared.component';
import { DialogEditUserRoleShared } from '@unity/shared/dialog-edit-user-role-shared/dialog-edit-user-role-shared';
import { GridcellUserRoleWorkflowActionsSharedComponent } from '@unity/shared/gridcell-user-role-workflow-actions-shared/gridcell-user-role-workflow-actions-shared.component';
import { ChipWorkflowStateSharedComponent } from '@unity/shared/presentation/chip-workflow-state-shared/chip-workflow-state-shared.component';
import { LinkGridcellSharedComponent } from '@unity/shared/presentation/link-gridcell-shared/link-gridcell-shared.component';
import { Paging, QuickActionProps, SortConfig } from '@unity/utils/types';
import { Observable, take, tap } from 'rxjs';
import { ActionSharedComponent } from '../composed/action-shared/action-shared.component';

@Component({
  selector: 'unity-grid-user-role-shared',
  standalone: true,
  imports: [
    KENDO_GRID,
    KENDO_DROPDOWNBUTTON,
    KENDO_LABEL,
    KENDO_DROPDOWNS,
    KENDO_BUTTON,
    KENDO_STACKLAYOUT,
    GridSharedComponent,
    LinkGridcellSharedComponent,
    ChipWorkflowStateSharedComponent,
    GridcellUserRoleWorkflowActionsSharedComponent,
    DialogEditUserRoleShared,
    AuthorizeDirective,
    TypedDirective,
    ActionSharedComponent,
  ],
  templateUrl: './grid-user-role-shared.component.html',
  styleUrl: './grid-user-role-shared.component.scss',
})
export class GridUserRoleSharedComponent extends AbstractGridComponent<UserRoleMiniDto> {
  private readonly transitionsClient = inject(TransitionsClient);
  private readonly userRolesClient = inject(UserRolesClient);

  protected override readonly resource = rxResource({
    params: () => {
      const query = this.$gridifyQuery();
      const userId = this.$userId();
      return query && userId ? { query, userId } : undefined;
    },
    stream: ({ params: { userId, query } }) =>
      this.userRolesClient.getAllForUserAsMini(userId, query.page, query.pageSize, query.orderBy, query.filter) as unknown as Observable<
        Paging<UserRoleMiniDto>
      >,
  });

  protected defaultSort: SortConfig[] = [
    {
      field: 'agency_name',
      dir: 'asc',
    },
  ];

  protected readonly $editingRole = signal<UserRoleMiniDto | null>(null);

  protected readonly changeRoleAction: QuickActionProps = {
    actionName: 'Change Role',
    description: 'Change role for this user',
    icon: 'tableCellPropertiesIcon',
  };

  public readonly $userId = input.required<number>({ alias: 'userId' });

  protected doTransition(data: { userRole: UserRoleMiniDto; transition: TransitionRequestDto }) {
    const { userRole, transition } = data;
    this.transitionsClient
      .setForUserRole(userRole.agency_id, userRole.user_id, userRole.id, transition)
      .pipe(
        take(1),
        tap(() => this.resource.reload())
      )
      .subscribe();
  }

  protected openRoleChangeDialog(userRole: UserRoleMiniDto) {
    this.$editingRole.set(userRole);
  }

  protected closeRoleChangeDialog() {
    this.$editingRole.set(null);
  }

  protected doRoleChange(data: { userRole: UserRoleMiniDto; update: UserRoleUpdateDto }) {
    const { userRole, update } = data;
    this.userRolesClient
      .updateOne(userRole.agency_id, userRole.user_id, userRole.id, update)
      .pipe(take(1))
      .subscribe(() => this.resource.reload());
  }
}
